import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "../../utils/portalHelpers";
import {
  EmptyState,
  ErrorPanel,
  LoadingPanel,
  PageHeader,
  Panel,
  StatusChip,
} from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  fetchPartnerNotifications,
  markAllPartnerNotificationsRead,
  markPartnerNotificationRead,
} from "../../api/endpoints/notifications";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { alertLabel, alertTone } from "./notificationHelpers";

const PAGE_SIZE = 25;

const READ_FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "read", label: "Read" },
];

/**
 * Everything the office has raised for this partner. Reading one here is what marks it read, and
 * a row only moves the partner somewhere the server said the portal can open.
 */
const NotificationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [readFilter, setReadFilter] = useState("all");
  const [page, setPage] = useState(0);

  const params = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      ...(readFilter === "unread" ? { unreadOnly: true } : {}),
      ...(readFilter === "read" ? { readOnly: true } : {}),
    }),
    [page, readFilter]
  );

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    partnerQueryKeys.notifications(params),
    () => fetchPartnerNotifications(params),
    { keepPreviousData: true }
  );

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(partnerQueryKeys.notificationCount());
  };

  const markOne = useMutation(markPartnerNotificationRead, { onSuccess: refresh });
  const markAll = useMutation(markAllPartnerNotificationsRead, {
    onSuccess: () => {
      setPage(0);
      refresh();
    },
  });

  const rows = data?.content || [];
  const totalPages = data?.totalPages ?? 0;

  const open = (notification) => {
    if (!notification.read) markOne.mutate(notification.id);
    if (notification.link) navigate(notification.link);
  };

  return (
    <div>
      <PageHeader
        title="Notifications"
        actions={
          <>
            <div
              role="group"
              aria-label="Filter by read state"
              className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 gap-0.5"
            >
              {READ_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  aria-pressed={readFilter === filter.id}
                  onClick={() => {
                    setReadFilter(filter.id);
                    setPage(0);
                  }}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    readFilter === filter.id
                      ? "bg-teal-700 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isLoading}
              className="rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
            >
              Mark all read
            </button>
          </>
        }
      />

      {isLoading ? (
        <LoadingPanel label="Loading your notifications…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            readFilter === "unread"
              ? "Nothing unread"
              : readFilter === "read"
              ? "No read notifications"
              : "Nothing yet"
          }
          description="Document requests, leads, tasks and payments will appear here as they happen."
        />
      ) : (
        <Panel
          title={`${data?.totalItems ?? rows.length} notification${rows.length === 1 ? "" : "s"}`}
        >
          <ul className="list-none p-0 m-0">
            {rows.map((notification) => (
              <li
                key={notification.id}
                className={`flex items-start justify-between gap-3 px-3 py-3 border-b border-slate-100 last:border-0 ${
                  notification.read ? "" : "bg-teal-50/40"
                }`}
              >
                <button
                  type="button"
                  onClick={() => open(notification)}
                  className="min-w-0 flex-1 text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  <p
                    className={`text-sm m-0 ${
                      notification.read ? "text-slate-700" : "font-semibold text-slate-900"
                    }`}
                  >
                    {notification.content}
                  </p>
                  <p className="text-xs text-slate-500 m-0 mt-0.5">
                    {formatDateTime(notification.createdAt)}
                    {notification.link ? " · tap to open" : ""}
                  </p>
                </button>
                <StatusChip tone={alertTone(notification)}>{alertLabel(notification)}</StatusChip>
              </li>
            ))}
          </ul>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-slate-100">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </Panel>
      )}

      <p className="text-xs text-slate-500 mt-4 mb-0">
        Messages with the office live in{" "}
        <Link to="/partner/alerts" className="font-semibold text-teal-700 no-underline">
          Alerts / Messages
        </Link>
        .
      </p>
    </div>
  );
};

export default NotificationsPage;
